import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/i18n";
import { ShoppingCart, Link2, Unlink, RefreshCw, AlertTriangle, Package2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface EcommerceConnection {
  id: string;
  platform: "shopify" | "woocommerce";
  storeName: string;
  storeUrl: string | null;
  status: "connected" | "error" | "syncing";
  productCount: number;
  lastSyncAt: string | null;
}

interface EcommerceProduct {
  id: string;
  externalId: string;
  title: string;
  price: number;
  currency: string;
  status: "active" | "draft" | "archived";
  imageUrl?: string;
  syncedAt: string;
}

const STATUS_TONE: Record<EcommerceConnection["status"], string> = {
  connected: "text-[var(--color-success)]",
  error: "text-[var(--color-error)]",
  syncing: "text-[var(--color-warning)]",
};

export default function EcommercePage() {
  const [connections, setConnections] = useState<EcommerceConnection[]>([]);
  const [products, setProducts] = useState<EcommerceProduct[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncingConnectionId, setIsSyncingConnectionId] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (connectionId: string | null) => {
    if (!connectionId) {
      setProducts([]);
      return;
    }

    try {
      const res = await apiFetch<{ data: EcommerceProduct[] }>(`/crm/ecommerce/${connectionId}/products`);
      setProducts(res.data ?? []);
    } catch (err) {
      setProducts([]);
      setError(err instanceof Error ? err.message : "Failed to load synced products.");
    }
  }, []);

  const fetchConnections = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: EcommerceConnection[] }>("/crm/ecommerce");
      const nextConnections = res.data ?? [];
      setConnections(nextConnections);
      setSelectedConnectionId((current) => current && nextConnections.some((connection) => connection.id === current)
        ? current
        : (nextConnections[0]?.id ?? null));
      setError(null);
    } catch (err) {
      setConnections([]);
      setSelectedConnectionId(null);
      setError(err instanceof Error ? err.message : "Failed to load ecommerce connections.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { void fetchConnections(); }, [fetchConnections]);
  useEffect(() => { void fetchProducts(selectedConnectionId); }, [fetchProducts, selectedConnectionId]);

  const handleConnectShopify = async () => {
    const storeInput = window.prompt("Enter your Shopify store domain (for example: stero-store.myshopify.com)");
    const shopDomain = storeInput?.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "") ?? "";
    if (!shopDomain) {
      return;
    }

    setIsConnecting("shopify");
    try {
      const res = await apiFetch<{ authUrl: string }>("/api/integrations/oauth/initiate", {
        method: "POST",
        body: JSON.stringify({ providerId: "shopify", shopDomain }),
      });
      window.location.href = res.authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to initiate Shopify connection.");
      setIsConnecting(null);
    }
  };

  const handleDisconnect = async (connectionId: string) => {
    const confirmed = window.confirm("Disconnect this store? Product sync will stop.");
    if (!confirmed) return;

    try {
      await apiFetch(`/api/integrations/connections/${connectionId}`, { method: "DELETE" });
      await fetchConnections();
      await fetchProducts(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disconnect the store.");
    }
  };

  const handleSync = async (connectionId: string) => {
    setIsSyncingConnectionId(connectionId);
    try {
      await apiFetch("/crm/ecommerce/sync", {
        method: "POST",
        body: JSON.stringify({ connectionId }),
      });
      await fetchConnections();
      await fetchProducts(connectionId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sync Shopify products.");
    } finally {
      setIsSyncingConnectionId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-20" />)}
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-8">
      <div className="flex items-center gap-3">
        <ShoppingCart className="h-5 w-5 text-[var(--color-primary)]" />
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">E-commerce</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">Mounted Shopify product sync using the canonical integration credentials seam.</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 p-4 text-sm text-[var(--color-error)]">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-[var(--color-foreground)]">Connected Stores</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void handleConnectShopify()}
              disabled={isConnecting === "shopify" || connections.some((connection) => connection.platform === "shopify")}
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:opacity-50"
            >
              <span className="inline-flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" />
                Connect Shopify
              </span>
            </button>
            <button
              type="button"
              disabled
              className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-muted-foreground)] opacity-60"
              title="WooCommerce product sync is outside this first slice."
            >
              Connect WooCommerce
            </button>
          </div>
        </div>

        {connections.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-10 text-center">
            <p className="font-medium text-[var(--color-foreground)]">No ecommerce stores connected yet</p>
            <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">Connect Shopify to start syncing products into the tenant catalog.</p>
          </div>
        ) : (
          connections.map((connection) => (
            <div key={connection.id} className="stagger-item flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-[var(--color-foreground)]">{connection.storeName}</p>
                  <span className={`text-xs font-medium capitalize ${STATUS_TONE[connection.status]}`}>{connection.status}</span>
                </div>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {connection.platform} · {connection.productCount} synced products
                  {connection.lastSyncAt ? ` · Last sync ${formatDateTime(connection.lastSyncAt)}` : " · Not synced yet"}
                </p>
                {connection.storeUrl && (
                  <a href={connection.storeUrl} target="_blank" rel="noreferrer" className="text-xs text-[var(--color-primary)] hover:underline">
                    {connection.storeUrl}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedConnectionId(connection.id)}
                  className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
                >
                  View products
                </button>
                <button
                  type="button"
                  onClick={() => void handleSync(connection.id)}
                  disabled={isSyncingConnectionId === connection.id}
                  className="rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-60"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <RefreshCw className={`h-3.5 w-3.5 ${isSyncingConnectionId === connection.id ? "animate-spin" : ""}`} />
                    Sync products
                  </span>
                </button>
                <button type="button" onClick={() => void handleDisconnect(connection.id)} className="text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]">
                  <Unlink className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      <section className="grid gap-4 lg:grid-cols-[2fr,1fr]">
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <div className="mb-4 flex items-center gap-2">
            <Package2 className="h-4 w-4 text-[var(--color-primary)]" />
            <h2 className="text-lg font-medium text-[var(--color-foreground)]">Synced Products</h2>
          </div>

          {selectedConnectionId === null ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">Choose a connected store to inspect the synced product catalog.</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">No products have been synced for this store yet.</p>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <div key={product.id} className="rounded-lg border border-[var(--color-border)] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-[var(--color-foreground)]">{product.title}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{product.externalId}</p>
                    </div>
                    <span className="text-sm font-medium text-[var(--color-foreground)]">{formatCurrency(product.price, product.currency)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
                    <span className="capitalize">{product.status}</span>
                     <span>Synced {formatDateTime(product.syncedAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
          <h2 className="text-lg font-medium text-[var(--color-foreground)]">Automation Follow-Through</h2>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Cart abandonment, post-purchase messaging, and external webhook automation remain later ecommerce slices. This first slice closes the truthful product-sync foundation only.
          </p>
        </div>
      </section>
    </div>
  );
}
